import produce from 'immer';
import {
  types,
  KioskState,
  KioskActionTypes,
  GetKioskAsyncSuccess,
  UpdateKioskAsyncSuccess,
  GetKioskAsyncError,
  UpdateKioskAsyncError,
  RebootKioskAsyncError,
  GetKioskAsyncPending,
  UpdateKioskAsyncPending,
  RebootKioskAsyncPending,
  UpdateKioskHardwareAsyncRequest,
  UpdateKioskHardwareAsyncSuccess,
  UpdateKioskHardwareAsyncPending,
  UpdateKioskHardwareAsyncError,
  Kiosk,
  CreatePrintTicketsAsyncRequest,
  CreatePrintTicketsAsyncSuccess,
  CreatePrintTicketsAsyncError,
  CreatePrintTicketsAsyncPending,
  CreateOrderTicketsAsyncSuccess,
  CreateOrderTicketsAsyncError,
  CreateOrderTicketsAsyncPending,
  CheckScheduleSuccess,
  UpdateTransactionsAsyncRequest,
  CheckKioskVersionAsyncSuccess,
  CheckKioskVersionAsyncPending,
  UpdateKioskAsyncWaiting,
  RebootAdminKioskAsyncWaiting,
  SetStatusActor,
  UpdateHardwareStatus
} from './types';

// Actions
export const actions = {
  getKiosk: (payload: { kioskId: string }): KioskActionTypes => ({
    type: types.GET_KIOSK_ASYNC.REQUEST,
    payload
  }),
  updateKiosk: (payload: Partial<Kiosk>): KioskActionTypes => ({
    type: types.UPDATE_KIOSK_ASYNC.REQUEST,
    payload
  }),
  rebootKiosk: () => ({
    type: types.REBOOT_KIOSK_ASYNC.REQUEST
  }),
  updateKioskHardware: (payload: UpdateKioskHardwareAsyncRequest['payload']): KioskActionTypes => ({
    type: types.UPDATE_KIOSK_HARDWARE_ASYNC.REQUEST,
    payload
  }),
  createPrintTickets: (payload: CreatePrintTicketsAsyncRequest['payload']): KioskActionTypes => ({
    type: types.CREATE_PRINT_TICKETS_ASYNC.REQUEST,
    payload
  }),
  createOrderTickets: (payload: any[]): KioskActionTypes => ({
    type: types.CREATE_ORDER_TICKETS_ASYNC.REQUEST,
    payload
  }),
  updateTransactions: (payload: UpdateTransactionsAsyncRequest['payload']): KioskActionTypes => ({
    type: types.UPDATE_TRANSACTIONS_ASYNC.REQUEST,
    payload
  }),
  checkSchedule: (): KioskActionTypes => ({ type: types.CHECK_SCHEDULE.REQUEST }),
  resetTickets: (): KioskActionTypes => ({ type: types.RESET_TICKETS }),
  reloadApplication: (): KioskActionTypes => ({ type: types.RELOAD_APPLICATION }),
  listenSocketServer: (payload: { kioskId: string; configurationId: string; clientId: string }): KioskActionTypes => ({
    type: types.LISTEN_SOCKET_SERVER,
    payload
  }),
  decrementStockCounts: (): KioskActionTypes => ({ type: types.DECREMENT_STOCK_COUNTS_ASYNC.REQUEST }),
  checkLowStockThresholds: (): KioskActionTypes => ({ type: types.CHECK_LOW_STOCK_THRESHOLDS }),
  checkVersion: (): KioskActionTypes => ({ type: types.CHECK_KIOSK_VERSION_ASYNC.REQUEST }),
  kioskStatusWaiting: (): KioskActionTypes => ({
    type: types.UPDATE_KIOSK_ASYNC.WAITING,
    payload: false
  }),
  kioskRebootPending: (): KioskActionTypes => ({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.REQUEST }),
  setStatusActor: (payload: SetStatusActor['payload']): KioskActionTypes => ({
    type: types.SET_STATUS_ACTOR,
    payload
  }),
  updateHardware: (payload: UpdateHardwareStatus['payload']): KioskActionTypes => ({
    type: types.UPDATE_HARDWARE_STATUS,
    payload
  }),
  resetKiosk: (): KioskActionTypes => ({ type: types.RESET_KIOSK })
};

// Initial State
export const initialState: KioskState = {
  kiosk: null,
  tickets: [],
  loading: false,
  error: undefined,
  version: undefined,
  statusActor: undefined,
  kioskWaiting: false,
  kioskRebootWaiting: false,
  inactiveKioskHardwareId: undefined
};

// Reducer
export const reducer = (state: KioskState = initialState, action: KioskActionTypes): KioskState => {
  return produce(state, (draft) => {
    let payload: any;
    switch (action.type) {
      case types.GET_KIOSK_ASYNC.SUCCESS:
        draft.kiosk = (action as GetKioskAsyncSuccess).payload;
        break;
      case types.UPDATE_KIOSK_ASYNC.SUCCESS:
        const updatedKiosk = (action as UpdateKioskAsyncSuccess).payload;
        // Update only the fields that were returned
        draft.kiosk = { ...draft.kiosk, ...updatedKiosk };
        break;
      case types.UPDATE_KIOSK_ASYNC.WAITING:
        draft.kioskWaiting = (action as UpdateKioskAsyncWaiting).payload;
        break;
      case types.REBOOT_ADMIN_KIOSK_ASYNC.WAITING:
        draft.kioskRebootWaiting = (action as RebootAdminKioskAsyncWaiting).payload;
        break;
      case types.UPDATE_KIOSK_HARDWARE_ASYNC.SUCCESS:
        if (draft.kiosk) {
          payload = (action as UpdateKioskHardwareAsyncSuccess).payload;
          const hardwareIndex = draft.kiosk.hardware.findIndex((hw) => hw.id === payload.id);
          if (hardwareIndex >= 0) {
            draft.kiosk.hardware[hardwareIndex] = payload;
          }
        }
        break;
      case types.CREATE_PRINT_TICKETS_ASYNC.SUCCESS:
      case types.CREATE_ORDER_TICKETS_ASYNC.SUCCESS:
        draft.tickets = (action as CreatePrintTicketsAsyncSuccess | CreateOrderTicketsAsyncSuccess).payload;
        break;
      case types.CHECK_SCHEDULE.SUCCESS:
        if (draft.kiosk) draft.kiosk.status = (action as CheckScheduleSuccess).payload;
        break;
      case types.CHECK_KIOSK_VERSION_ASYNC.SUCCESS:
        draft.version = (action as CheckKioskVersionAsyncSuccess).payload;
        break;
      case types.CHECK_KIOSK_VERSION_ASYNC.PENDING:
        if ((action as CheckKioskVersionAsyncPending).payload) {
          draft.version = undefined;
        }
        break;
      case types.SET_STATUS_ACTOR:
        draft.statusActor = (action as SetStatusActor).payload;
        break;
      case types.UPDATE_HARDWARE_STATUS:
        payload = (action as UpdateHardwareStatus).payload;
        if (draft.kiosk) {
          draft.kiosk.hardware.forEach((hw) => {
            if (hw.id === payload.hardwareId) {
              hw.status = payload.status;
            }
          });
          if (payload.status === 'Inactive') {
            draft.inactiveKioskHardwareId = payload.hardwareId;
          } else if (payload.status === 'Active' && payload.hardwareId === draft.inactiveKioskHardwareId) {
            draft.inactiveKioskHardwareId = undefined;
          }
        }
        break;
      case types.GET_KIOSK_ASYNC.ERROR:
      case types.UPDATE_KIOSK_ASYNC.ERROR:
      case types.REBOOT_KIOSK_ASYNC.ERROR:
      case types.UPDATE_KIOSK_HARDWARE_ASYNC.ERROR:
      case types.CREATE_PRINT_TICKETS_ASYNC.ERROR:
      case types.CREATE_ORDER_TICKETS_ASYNC.ERROR:
        draft.error = (
          action as
            | GetKioskAsyncError
            | UpdateKioskAsyncError
            | RebootKioskAsyncError
            | UpdateKioskHardwareAsyncError
            | CreatePrintTicketsAsyncError
            | CreateOrderTicketsAsyncError
        ).payload;
        break;
      case types.GET_KIOSK_ASYNC.PENDING:
      case types.UPDATE_KIOSK_ASYNC.PENDING:
      case types.REBOOT_KIOSK_ASYNC.PENDING:
      case types.UPDATE_KIOSK_HARDWARE_ASYNC.PENDING:
      case types.CREATE_PRINT_TICKETS_ASYNC.PENDING:
      case types.CREATE_ORDER_TICKETS_ASYNC.PENDING:
        draft.loading = (
          action as
            | GetKioskAsyncPending
            | UpdateKioskAsyncPending
            | RebootKioskAsyncPending
            | UpdateKioskHardwareAsyncPending
            | CreatePrintTicketsAsyncPending
            | CreateOrderTicketsAsyncPending
        ).payload;
        break;
      case types.RESET_TICKETS:
        draft.tickets = [];
        draft.error = undefined;
        draft.loading = false;
        break;
      case types.RESET_KIOSK:
        draft.kiosk = initialState.kiosk;
        draft.tickets = initialState.tickets;
        draft.loading = initialState.loading;
        draft.error = initialState.error;
        break;
      default:
    }
  });
};
